import { z } from 'zod';
import type { Schemas } from '../../api/client';
import type { Variant } from '../../components/pickers';

/** Form schemas and payload mappers for stock adjustments, write-offs and opening stock. */

const QTY_RE = /^\d{1,15}(\.\d{1,4})?$/;
const COST_RE = /^\d{1,15}(\.\d{1,6})?$/;

export const qtyField = z
  .string()
  .trim()
  .min(1, 'Enter a quantity')
  .regex(QTY_RE, 'A positive number, up to 4 decimals')
  .refine((v) => Number(v) > 0, 'Must be greater than zero');

export type AdjustMode = 'ADJUSTMENT' | 'WRITE_OFF' | 'OPENING';

export const stockLineSchema = z.object({
  variant: z.custom<Variant | null>().refine((v): boolean => v !== null, 'Pick an item'),
  /** ADJUSTMENT only: found (+) or missing (−). */
  direction: z.enum(['in', 'out']),
  qty: qtyField,
  unitCost: z.string().trim(),
});

export type StockLineValues = z.infer<typeof stockLineSchema>;

export function stockFormSchema(mode: AdjustMode) {
  return z
    .object({
      mode: z.enum(['ADJUSTMENT', 'WRITE_OFF', 'OPENING']),
      locationId: z.string().min(1, 'Pick a location'),
      businessDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Pick a date'),
      reason:
        mode === 'OPENING'
          ? z.string().trim().max(500)
          : z.string().trim().min(3, 'Say why stock is changing').max(500),
      lines: z.array(stockLineSchema).min(1, 'Add at least one item'),
    })
    .superRefine((values, ctx) => {
      const seen = new Map<string, number>();
      values.lines.forEach((line, i) => {
        const cost = line.unitCost;
        const costMatters =
          values.mode === 'OPENING' || (values.mode === 'ADJUSTMENT' && line.direction === 'in');
        if (values.mode === 'OPENING' && cost === '')
          ctx.addIssue({
            code: 'custom',
            path: ['lines', i, 'unitCost'],
            message: 'Enter the cost',
          });
        else if (costMatters && cost !== '' && !COST_RE.test(cost))
          ctx.addIssue({
            code: 'custom',
            path: ['lines', i, 'unitCost'],
            message: 'A positive amount, up to 6 decimals',
          });
        const v = line.variant;
        if (v) {
          const prev = seen.get(v.id);
          if (prev !== undefined)
            ctx.addIssue({
              code: 'custom',
              path: ['lines', i, 'variant'],
              message: `Already on line ${String(prev + 1)}`,
            });
          else seen.set(v.id, i);
          if (
            !v.baseUnit.allowsFraction &&
            QTY_RE.test(line.qty) &&
            !Number.isInteger(Number(line.qty))
          )
            ctx.addIssue({
              code: 'custom',
              path: ['lines', i, 'qty'],
              message: `Whole ${v.baseUnit.symbol} only`,
            });
        }
      });
    });
}

export type StockFormValues = z.infer<ReturnType<typeof stockFormSchema>>;

export const emptyLine = (): StockLineValues => ({
  variant: null,
  direction: 'in',
  qty: '',
  unitCost: '',
});

/** Payload for POST /stock/adjustments (ADJUSTMENT is signed; WRITE_OFF is the positive qty removed). */
export function toAdjustmentRequest(
  values: StockFormValues,
  id?: string,
): Schemas['StockAdjustmentRequest'] {
  if (values.mode === 'OPENING') throw new Error('Use toOpeningRequest for opening stock');
  return {
    ...(id ? { id } : {}),
    kind: values.mode,
    locationId: values.locationId,
    businessDate: values.businessDate,
    reason: values.reason.trim(),
    lines: values.lines.map((l) => {
      const out = values.mode === 'ADJUSTMENT' && l.direction === 'out';
      const withCost = values.mode === 'ADJUSTMENT' && !out && l.unitCost !== '';
      return {
        variantId: (l.variant as Variant).id,
        qty: out ? `-${l.qty}` : l.qty,
        ...(withCost ? { unitCost: l.unitCost } : {}),
      };
    }),
  };
}

/** Payload for POST /stock/opening. */
export function toOpeningRequest(values: StockFormValues): Schemas['OpeningStockRequest'] {
  return {
    locationId: values.locationId,
    businessDate: values.businessDate,
    reason: values.reason.trim() || 'Opening stock',
    lines: values.lines.map((l) => ({
      variantId: (l.variant as Variant).id,
      qty: l.qty,
      unitCost: l.unitCost,
    })),
  };
}

/** Signed change a line will make to stock (for the live "after" column). */
export function lineDelta(
  mode: AdjustMode,
  line: Pick<StockLineValues, 'direction' | 'qty'>,
): number {
  const n = Number(line.qty);
  if (!Number.isFinite(n) || n <= 0) return 0;
  if (mode === 'WRITE_OFF') return -n;
  if (mode === 'ADJUSTMENT' && line.direction === 'out') return -n;
  return n;
}

/** Counted quantities: a blank field means "not counted" (null), otherwise a non-negative number. */
export function parseCounted(value: string): { ok: true; value: string | null } | { ok: false } {
  const v = value.trim();
  if (v === '') return { ok: true, value: null };
  return QTY_RE.test(v) ? { ok: true, value: v } : { ok: false };
}

export interface CountLineLike {
  systemQty: string;
  countedQty: string | null;
}

/** Variance summary for a count (difference = counted − system; uncounted lines are ignored). */
export function countSummary(lines: CountLineLike[]) {
  let counted = 0;
  let variances = 0;
  let over = 0;
  let short = 0;
  for (const l of lines) {
    if (l.countedQty === null) continue;
    counted += 1;
    const d = Number(l.countedQty) - Number(l.systemQty);
    if (d !== 0) variances += 1;
    if (d > 0) over += d;
    if (d < 0) short += -d;
  }
  return {
    total: lines.length,
    counted,
    uncounted: lines.length - counted,
    variances,
    over,
    short,
  };
}
